import { Box } from '@material-ui/core'
import { Point, LineSeries, SliceData } from '@nivo/line'
import { addHours, differenceInHours } from 'date-fns'
import { intersectionWith } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BatteryProvider } from '../../../calculators/providers/BatteryProvider'
import { formatEnergyCapacity } from '../../../util/formatters'
import { LineChart } from '../../common/charts/LineChart'
import { Section } from '../../common/Section'
import { FormGroup } from '../../form/FormGroup'
import {
  createGetValuesByResolution,
  getEndDateTime,
  mapValuesToDataPoints,
} from './chart-util'
import { ChartHourSeriesSelect } from './ChartHourSeriesSelect'
import { ChartTimePicker, getStartDateByTimeSpan } from './ChartTimePicker'
import {
  ChartTimeResolutionSelect,
  TimeResolution,
} from './ChartTimeResolutionSelect'
import { ChartTimeSpanSelect, TimeSpan } from './ChartTimeSpanSelect'
import { ChartType } from './ChartTypeToggle'
import { BatteryFlow, useBatteryChartUtil } from './use-battery-chart-util'
import { useTimeChartFormatters } from './use-time-chart-formatters'

type Props = {
  providers: BatteryProvider[]
  startOfYearDate: Date
}

export function BatteryChart({
  providers,
  startOfYearDate,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const { batteryFlows, defaultFlows, options } = useBatteryChartUtil(providers)

  const [selectedFlows, setSelectedFlows] =
    useState<BatteryFlow[]>(defaultFlows)
  const [graphNames, setGraphNames] = useState<string[]>(
    defaultFlows.map((flow) => flow.name),
  )
  const [resolution, setResolution] = useState(TimeResolution.Hour)
  const [timeSpan, setTimeSpan] = useState(TimeSpan.Year)
  const [selectedDate, setSelectedDate] = useState(
    getStartDateByTimeSpan(startOfYearDate, timeSpan),
  )

  const { getXAxisValueFormatter, getTimeFormatter } = useTimeChartFormatters(
    timeSpan,
    selectedDate,
  )

  const handleTimeSpanChange = (value: TimeSpan, date?: Date) => {
    setTimeSpan(value)
    setSelectedDate(getStartDateByTimeSpan(date ?? selectedDate, value))
  }

  const handleSelectChange = useCallback(
    (values: string[]) => {
      setGraphNames(values)
      const newFlows = intersectionWith(
        batteryFlows,
        values,
        (flow, name) => flow.name === name,
      )
      setSelectedFlows(newFlows)
    },
    [batteryFlows],
  )

  const getValuesByResolution = createGetValuesByResolution(startOfYearDate)

  const mapToGraphData = (flow: BatteryFlow) => {
    const startHourIndex = differenceInHours(selectedDate, startOfYearDate)
    const endHourIndex = differenceInHours(
      getEndDateTime(timeSpan, selectedDate),
      startOfYearDate,
    )
    const values = [...flow.hourSeries].slice(startHourIndex, endHourIndex)
    const summedValues = getValuesByResolution(values, resolution)

    return {
      id: flow.name,
      data: summedValues.map(mapValuesToDataPoints),
    }
  }

  useEffect(() => {
    setSelectedFlows(defaultFlows)
    setGraphNames(defaultFlows.map((flow) => flow.name))
    // Using defaultFlows as dependency causes render loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providers])

  const data = selectedFlows.map(mapToGraphData)

  const onClickHandler = (
    datum: Readonly<Point<LineSeries>> | Readonly<SliceData<LineSeries>>,
  ): void => {
    if ('data' in datum && typeof datum.data.x === 'number') {
      const hour = Number(datum.data.x)
      if (timeSpan === TimeSpan.Year) {
        const newDate = addHours(selectedDate, hour)
        handleTimeSpanChange(TimeSpan.Month, newDate)
      } else if (timeSpan === TimeSpan.Month) {
        const newDate = addHours(selectedDate, hour)
        handleTimeSpanChange(TimeSpan.Day, newDate)
      } else {
        handleTimeSpanChange(TimeSpan.Year, startOfYearDate)
      }
    }
  }

  return (
    <Section title={t('charts.battery.title')}>
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Box>
          <FormGroup mr={2}>
            <ChartHourSeriesSelect
              values={graphNames}
              onChange={handleSelectChange}
              options={options}
            />
          </FormGroup>
        </Box>

        <Box display="flex" flexDirection="row">
          <FormGroup>
            <ChartTimeResolutionSelect
              value={resolution}
              onChange={setResolution}
            />
          </FormGroup>

          <FormGroup ml={2}>
            <ChartTimeSpanSelect
              value={timeSpan}
              onChange={handleTimeSpanChange}
              chartType={ChartType.Power}
            />
          </FormGroup>

          <FormGroup ml={2}>
            <ChartTimePicker
              timeSpan={timeSpan}
              selectedDate={selectedDate}
              onChange={setSelectedDate}
              startOfYearDate={startOfYearDate}
            />
          </FormGroup>
        </Box>
      </Box>
      <Box width="100%" height={540} borderRadius={6}>
        <LineChart
          data={data}
          xFormatter={getTimeFormatter(resolution)}
          yFormatter={formatEnergyCapacity}
          xAxisFormatter={getXAxisValueFormatter(resolution)}
          onClick={onClickHandler}
        />
      </Box>
    </Section>
  )
}
